import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  HiPlus,
  HiSearch,
  HiPrinter,
  HiPencil,
  HiTrash,
  HiX,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi';
import { toPng } from 'html-to-image';

import apiService from '../services/api';
import ClientSearchSelect from './ClientSearchSelect';
import Select from './Select';
import ConfirmDialog from './ConfirmDialog';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import { SkeletonTable } from './Skeleton';
import { PERMISSAO_MAP } from '../types';
import { normalizeApiData } from '../utils/apiHelpers';
import {
  fetchAllPackageCodigos,
  nextPackageCodeForPrefix,
} from '../utils/packageCodeSequence';

import './Boxes.css';
import './Coletas.css';

/** Para ordenação: timestamp da data de coleta (ou criação). */
const dataColetaMs = (row) => {
  const raw = row.data_coleta || row.created_at || row.updated_at || '';
  if (!raw) return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const parseWeight = (weightStr) => {
  if (!weightStr) return 0;
  const cleaned = weightStr.toString().trim().replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const formatCurrency = (num) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number(num) || 0
  );

/**
 * Número da tarifa como vem do GET /api/v1/tarifas:
 * - `number` → uso direto
 * - string pt-BR "1.234,56" → milhar com ponto, decimal com vírgula
 * - string JSON "1234.56" / "10.5" → ponto como decimal (não remover como milhar)
 */
const parseTarifaNumber = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') {
    return Number.isFinite(v) ? v : 0;
  }
  const raw = String(v).trim().replace(/[R$\s\u00a0]/g, '');
  if (!raw) return 0;
  if (raw.includes(',')) {
    const cleaned = raw.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** ID da tarifa padrão quando Gerente cria coleta (sem selecionar tarifa na UI). */
const DEFAULT_GERENTE_TARIFA_ID = 1;

/** Valor da remessa (R$/kg) × peso quando Gerente cria e não há valor preenchido/calculado. */
const GERENTE_VALOR_REMESA_POR_KG = 65;

const Coletas = () => {
  const { notifications, showSuccess, showWarning, removeNotification } =
    useNotification();
  const { userRole, loading: permissionsLoading } = usePermissions();
  const isColetasAdmin = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    return PERMISSAO_MAP[Number(userRole.permissaos_id)] === 'Admin';
  }, [userRole, permissionsLoading]);
  const canCreateEditColetas = useMemo(() => {
    if (permissionsLoading) return false;
    if (!userRole) return false;
    const role = PERMISSAO_MAP[Number(userRole.permissaos_id)];
    return role === 'Admin' || role === 'Gerente';
  }, [userRole, permissionsLoading]);

  const [coletas, setColetas] = useState([]);
  const [clients, setClients] = useState([]);
  const [tarifas, setTarifas] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [lastPage, setLastPage] = useState(1);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const initialFormData = useMemo(
    () => ({
      codigo_pacote: '',
      status: '',
      cliente_id: '',
      cliente_nome: '',
      nome: '',
      email: '',
      telefone: '',
      documento: '',
      endereco: '',

      origem: '',
      destino: '',
      peso: '',
      tarifa_id: '',
      valor_remessa: '',

      codigos_acesso: { cliente: '' },
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormData);

  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedColeta, setSelectedColeta] = useState(null);
  const [savingLabelImage, setSavingLabelImage] = useState(false);

  const labelRef = useRef(null);

  const generateAccessCode = (prefix = '') => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = prefix;
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const generateClientAccessCode = () => ({ cliente: generateAccessCode('CLI') });

  const calculateValorRemessa = (tarifaId, pesoStr) => {
    if (!tarifaId || !pesoStr) return '';
    const pesoNum = parseWeight(pesoStr);
    if (!pesoNum) return '';

    const tarifa = tarifas.find((t) => String(t.id) === String(tarifaId));
    if (!tarifa) return '';

    const valorTarifa = parseTarifaNumber(tarifa.valor);
    const acresc = parseTarifaNumber(tarifa.acrescimento_embalagem);
    const total = valorTarifa * pesoNum + acresc;
    return formatCurrency(total);
  };

  const loadClients = async () => {
    try {
      const perPage = 500;
      let page = 1;
      const all = [];
      for (;;) {
        const response = await apiService.getClientsList({
          page,
          per_page: perPage,
        });
        const list = normalizeApiData(response);
        if (!Array.isArray(list) || list.length === 0) break;
        all.push(...list);
        const nextLastPage = response?.last_page ?? response?.lastPage;
        if (nextLastPage !== undefined && nextLastPage !== null) {
          if (page >= Number(nextLastPage)) break;
          page += 1;
          continue;
        }
        if (list.length < perPage) break;
        page += 1;
      }

      setClients(
        all.map((c) => ({
          id: c.id,
          nome: c.nome || c.name || '',
          email: c.email || '',
          telefone: c.telefone || c.telefone || c.telefone_destinatario || c.telefone_cliente || c.telefone || '',
          documento: c.documento || c.cnpj || '',
          endereco: c.endereco || '',
        }))
      );
    } catch (e) {
      console.error('Erro ao carregar clientes:', e);
      setClients([]);
    }
  };

  const loadTarifas = async () => {
    try {
      const data = await apiService.getTarifas();
      const tarifasList = normalizeApiData(data);
      setTarifas(Array.isArray(tarifasList) ? tarifasList : []);
    } catch (e) {
      console.error('Erro ao carregar tarifas:', e);
      setTarifas([]);
    }
  };

  const loadColetas = async ({ page, per_page, search } = {}) => {
    const reqPage = page !== undefined ? page : currentPage;
    const reqPerPage = per_page !== undefined ? per_page : itemsPerPage;
    const reqSearch = search !== undefined ? search : searchTerm;

    try {
      const response = await apiService.getColetas({
        page: reqPage,
        per_page: reqPerPage,
        ...(reqSearch && reqSearch.trim() ? { search: reqSearch.trim() } : {}),
      });

      const list = normalizeApiData(response);
      const mapped = (Array.isArray(list) ? list : []).map((box) => {
        let codigos = box.codigos_acesso;
        if (typeof codigos === 'string' && codigos.trim() !== '') {
          try {
            codigos = JSON.parse(codigos);
          } catch {
            codigos = null;
          }
        }

        return {
          ...box,
          codigo: box.codigo_pacote || box.codigo || '',
          cliente_id: box.cliente_id || box.cliente?.id || '',
          cliente_nome:
            box.cliente_nome ||
            box.cliente?.nome ||
            box.cliente?.name ||
            box.cliente_name ||
            '',
          origem: box.origem || '',
          destino: box.destino || '',
          peso_bruto: (box.peso_bruto ?? box.peso) || '',
          valor_remessa: box.valor_remessa || box.valor_declarado || '',
          tarifa_id: box.tarifa_id || '',
          data_coleta: box.data_coleta || box.created_at || box.updated_at || '',
          codigos_acesso:
            codigos && typeof codigos === 'object' ? { cliente: codigos?.cliente || '' } : { cliente: '' },
        };
      });

      mapped.sort((a, b) => dataColetaMs(b) - dataColetaMs(a));

      setColetas(mapped);
      setLastPage(response?.last_page ?? 1);
      if (page !== undefined) setCurrentPage(page);
      if (per_page !== undefined) setItemsPerPage(per_page);
      if (search !== undefined) setSearchTerm(search);
      setLoading(false);
    } catch (e) {
      console.error('Erro ao carregar coletas:', e);
      setColetas([]);
      setLastPage(1);
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      setLoading(true);
      try {
        await Promise.all([loadClients(), loadTarifas()]);
        if (!cancelled) {
          await loadColetas({ page: 1, per_page: itemsPerPage, search: '' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showCreateModal) return;
    // Prefere ressincronizar valores de cliente quando abrir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal]);

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingId(null);
    setFormData(initialFormData);
  };

  const openCreate = () => {
    if (!canCreateEditColetas) return;
    setEditingId(null);
    const tid = isColetasAdmin ? '' : String(DEFAULT_GERENTE_TARIFA_ID);
    const t = !isColetasAdmin && tid
      ? tarifas.find((x) => String(x.id) === tid)
      : null;
    setFormData({
      ...initialFormData,
      tarifa_id: tid,
      origem: t?.origem || '',
      destino: t?.destino || '',
    });
    setShowCreateModal(true);
  };

  const openEdit = (c) => {
    if (!canCreateEditColetas) return;
    const pesoRaw = c.peso_bruto ?? c.peso;
    let pesoStr = '';
    if (pesoRaw !== null && pesoRaw !== undefined && pesoRaw !== '') {
      const n =
        typeof pesoRaw === 'number'
          ? pesoRaw
          : parseFloat(String(pesoRaw).replace(',', '.'));
      pesoStr = !Number.isNaN(n) ? n.toFixed(2).replace('.', ',') : String(pesoRaw);
    }
    const vr = c.valor_remessa || c.valor_declarado;
    const valorFormatted =
      vr !== null && vr !== undefined && vr !== ''
        ? formatCurrency(parseTarifaNumber(vr))
        : '';

    const cl = clients.find((x) => String(x.id) === String(c.cliente_id));

    setEditingId(c.id);
    setFormData({
      ...initialFormData,
      codigo_pacote: c.codigo || c.codigo_pacote || '',
      status: c.status || 'pedido_criado',
      cliente_id: String(c.cliente_id || ''),
      cliente_nome:
        c.cliente_nome ||
        c.cliente?.nome ||
        c.cliente?.name ||
        cl?.nome ||
        '',
      nome: c.nome_destinatario || cl?.nome || '',
      email: cl?.email || '',
      telefone: c.telefone_destinatario || cl?.telefone || '',
      documento: cl?.documento || '',
      endereco: cl?.endereco || '',
      origem: c.origem || '',
      destino: c.destino || '',
      peso: pesoStr,
      tarifa_id: c.tarifa_id ? String(c.tarifa_id) : '',
      valor_remessa: valorFormatted,
      codigos_acesso:
        c.codigos_acesso && typeof c.codigos_acesso === 'object'
          ? { cliente: c.codigos_acesso.cliente || '' }
          : { cliente: '' },
    });
    setShowCreateModal(true);
  };

  const confirmDeleteColeta = async () => {
    if (!deleteTargetId) return;
    try {
      await apiService.deleteColeta(deleteTargetId);
      showSuccess('Coleta excluída com sucesso!');
      setDeleteTargetId(null);
      await loadColetas({
        page: currentPage,
        per_page: itemsPerPage,
        search: searchTerm,
      });
    } catch (err) {
      showWarning(err?.message || 'Erro ao excluir coleta');
      setDeleteTargetId(null);
    }
  };

  const handleClientChange = (payload) => {
    const clienteId = payload?.cliente_id || '';
    const c = clients.find((x) => String(x.id) === String(clienteId));

    setFormData((prev) => ({
      ...prev,
      cliente_id: clienteId,
      cliente_nome: payload?.cliente_nome || '',
      nome: c?.nome || payload?.cliente_nome || '',
      email: payload?.cliente_email || c?.email || '',
      telefone: payload?.cliente_telefone || c?.telefone || '',
      documento: c?.documento || payload?.cliente_documento || '',
      endereco: c?.endereco || payload?.cliente_endereco || '',
    }));
  };

  const handleTarifaChange = (tarifaId) => {
    if (!isColetasAdmin) return;
    const t = tarifas.find((x) => String(x.id) === String(tarifaId));
    setFormData((prev) => {
      const valor = calculateValorRemessa(tarifaId, prev.peso);
      return {
        ...prev,
        tarifa_id: tarifaId,
        origem: t?.origem || '',
        destino: t?.destino || '',
        valor_remessa: valor,
      };
    });
  };

  const handlePesoChange = (peso) => {
    setFormData((prev) => {
      let valor = prev.tarifa_id ? calculateValorRemessa(prev.tarifa_id, peso) : '';
      const t = prev.tarifa_id
        ? tarifas.find((x) => String(x.id) === String(prev.tarifa_id))
        : null;
      const w = parseWeight(peso);
      if (
        !isColetasAdmin &&
        w > 0 &&
        (!String(valor).trim() || parseTarifaNumber(valor) <= 0)
      ) {
        valor = formatCurrency(GERENTE_VALOR_REMESA_POR_KG * w);
      }
      return {
        ...prev,
        peso,
        valor_remessa: valor,
        origem: prev.origem || t?.origem || '',
        destino: prev.destino || t?.destino || '',
      };
    });
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!formData.cliente_id) {
      showWarning('Selecione um cliente para a coleta.');
      return;
    }
    if (formData.tarifa_id && !formData.peso) {
      showWarning('Informe o peso antes de selecionar uma tarifa.');
      return;
    }

    try {
      setSaving(true);
      const pesoNum = parseWeight(formData.peso);
      const codigosAcesso = editingId
        ? formData.codigos_acesso
        : generateClientAccessCode();

      let codigoPacote = formData.codigo_pacote;
      if (!editingId) {
        const codes = await fetchAllPackageCodigos((params) =>
          apiService.getColetas(params)
        );
        codigoPacote = nextPackageCodeForPrefix(codes, 'CX');
      }

      let valorNum = formData.valor_remessa
        ? parseTarifaNumber(formData.valor_remessa)
        : null;
      if (!editingId && !isColetasAdmin) {
        if (valorNum === null || Number.isNaN(valorNum) || valorNum <= 0) {
          if (pesoNum > 0) {
            valorNum = GERENTE_VALOR_REMESA_POR_KG * pesoNum;
          } else {
            valorNum = null;
          }
        }
      }

      const coletaPayload = {
        codigo_pacote: codigoPacote,
        tipo: 'caixa',
        cliente_id: formData.cliente_id,
        status: editingId ? formData.status || 'pedido_criado' : 'pedido_criado',
        peso_bruto: pesoNum || null,
        valor_remessa:
          valorNum !== null && !Number.isNaN(valorNum) ? valorNum : null,
        tarifa_id: editingId
          ? formData.tarifa_id || null
          : isColetasAdmin
            ? formData.tarifa_id || null
            : String(DEFAULT_GERENTE_TARIFA_ID),
        origem: formData.origem || '',
        destino: formData.destino || '',
        nome_destinatario: formData.nome || '',
        telefone_destinatario: formData.telefone || '',
        codigos_acesso: codigosAcesso,
      };

      const reloadPage = editingId ? currentPage : 1;
      if (editingId) {
        await apiService.updateColeta(editingId, coletaPayload);
        showSuccess('Coleta atualizada com sucesso!');
      } else {
        await apiService.createColeta(coletaPayload);
        showSuccess('Coleta criada com sucesso!');
      }

      closeModal();
      await loadColetas({
        page: reloadPage,
        per_page: itemsPerPage,
        search: searchTerm,
      });
    } catch (err) {
      console.error('Erro ao salvar coleta:', err);
      showWarning(err?.message || (editingId ? 'Erro ao atualizar coleta' : 'Erro ao criar coleta'));
    } finally {
      setSaving(false);
    }
  };

  const openPrintLabel = (coleta) => {
    setSelectedColeta(coleta);
    setShowLabelModal(true);
  };

  const labelClient = useMemo(() => {
    if (!selectedColeta) return null;
    const c = clients.find((x) => String(x.id) === String(selectedColeta.cliente_id));
    return c || null;
  }, [clients, selectedColeta]);

  const handleSaveLabelAsPng = async () => {
    const node = labelRef.current;
    if (!node) {
      showWarning('Área da etiqueta não encontrada.');
      return;
    }
    try {
      setSavingLabelImage(true);
      const dataUrl = await toPng(node, {
        pixelRatio: 4,
        cacheBust: true,
        backgroundColor: '#ffffff',
      });

      const pacote = (selectedColeta?.codigo || '').toString().replace(/[^\w-]/g, '_');
      const cliente = (labelClient?.nome || 'coleta').toString().replace(/[^\w-]/g, '_').slice(0, 48);

      const link = document.createElement('a');
      link.download = `coleta-${pacote || cliente}.png`;
      link.href = dataUrl;
      link.click();
      showSuccess('Etiqueta salva com sucesso.');
    } catch (err) {
      console.error(err);
      showWarning(err?.message || 'Não foi possível gerar a imagem.');
    } finally {
      setSavingLabelImage(false);
    }
  };

  const formatDataColeta = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /** Data/hora compacta para a etiqueta (fonte pequena). */
  const formatDataColetaEtiqueta = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const displayValorColeta = (c) => {
    const raw = c.valor_remessa ?? c.valor_declarado;
    if (raw === null || raw === undefined || raw === '') return '—';
    return formatCurrency(parseTarifaNumber(raw));
  };

  const statusForWeight = (value) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return n.toFixed(2).replace('.', ',');
  };

  const nomeEtiquetaColeta =
    selectedColeta?.nome_destinatario ||
    selectedColeta?.cliente_nome ||
    labelClient?.nome ||
    '';

  const dataColetaEtiquetaStr = selectedColeta
    ? formatDataColetaEtiqueta(
        selectedColeta.data_coleta ||
          selectedColeta.created_at ||
          selectedColeta.updated_at
      )
    : '';

  return (
    <div className="page-container">
      {notifications.map((n) => (
        <div
          key={n.id}
          className={`notification notification--${n.type}`}
          onClick={() => removeNotification?.(n.id)}
        >
          {n.message}
        </div>
      ))}

      <div className="breadcrumb">
        <span>Painel</span>
        <span>Coletas</span>
      </div>

      <div className="page-header">
        <div>
          <h1>Coletas</h1>
          <p>Crie e imprima a etiqueta de coleta</p>
        </div>
        {canCreateEditColetas && (
          <button className="btn btn-primary" onClick={openCreate}>
            <HiPlus size={20} />
            Nova Coleta
          </button>
        )}
      </div>

      <div className="page-actions">
        <div className="search-box">
          <HiSearch className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Buscar coletas..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadColetas({ page: 1, per_page: itemsPerPage, search: searchTerm });
            }}
          />
        </div>
      </div>

      <div className="card">
        {loading ? (
          <SkeletonTable rows={8} columns={isColetasAdmin ? 7 : 6} />
        ) : !Array.isArray(coletas) || coletas.length === 0 ? (
          <div className="empty-state">
            <p>{searchTerm ? 'Nenhuma coleta encontrada' : 'Nenhuma coleta cadastrada'}</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Cliente</th>
                  <th>Destino</th>
                  <th>Peso (kg)</th>
                  {isColetasAdmin && <th>Valor</th>}
                  <th>Data Coleta</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {coletas.map((c, idx) => (
                  <tr key={c.id || c.codigo || idx}>
                    <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    <td>{c.cliente_nome || '—'}</td>
                    <td>{c.destino || '—'}</td>
                    <td>{statusForWeight(c.peso_bruto || c.peso || '')}</td>
                    {isColetasAdmin && (
                      <td>
                        <strong>{displayValorColeta(c)}</strong>
                      </td>
                    )}
                    <td>{formatDataColeta(c.data_coleta)}</td>
                    <td>
                      <div
                        className="coletas-table-actions"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}
                      >
                        {canCreateEditColetas && (
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              if (!c.id) {
                                showWarning('Não foi possível identificar este registro para edição.');
                                return;
                              }
                              openEdit(c);
                            }}
                            title="Editar coleta"
                          >
                            <HiPencil size={18} />
                          </button>
                        )}
                        {isColetasAdmin && (
                          <button
                            type="button"
                            className="btn-icon"
                            onClick={() => {
                              if (!c.id) {
                                showWarning('Não foi possível identificar este registro para exclusão.');
                                return;
                              }
                              setDeleteTargetId(c.id);
                            }}
                            title="Excluir coleta"
                          >
                            <HiTrash size={18} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => openPrintLabel(c)}
                          title="Imprimir etiqueta de coleta"
                        >
                          <HiPrinter size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="pagination-container">
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => {
                    const prevPage = Math.max(1, currentPage - 1);
                    setCurrentPage(prevPage);
                    loadColetas({ page: prevPage, per_page: itemsPerPage, search: searchTerm });
                  }}
                  disabled={currentPage === 1}
                >
                  <HiChevronLeft />
                </button>
                <span className="pagination-info">
                  Página {currentPage} de {lastPage || 1}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => {
                    const nextPage = Math.min(lastPage, currentPage + 1);
                    setCurrentPage(nextPage);
                    loadColetas({ page: nextPage, per_page: itemsPerPage, search: searchTerm });
                  }}
                  disabled={currentPage === lastPage || lastPage === 0}
                >
                  <HiChevronRight />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Create / Edit */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Editar Coleta' : 'Nova Coleta'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <HiX size={24} />
              </button>
            </div>

            <form className="modal-form" onSubmit={handleSubmitCreate}>
              {editingId && formData.codigo_pacote && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Código</label>
                    <input
                      type="text"
                      value={formData.codigo_pacote}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Nome do Cliente</label>
                  <ClientSearchSelect
                    id="field-cliente_id"
                    value={formData.cliente_id}
                    selectedNome={formData.cliente_nome}
                    clients={clients}
                    onRefreshClients={loadClients}
                    placeholder="Selecione ou busque um cliente"
                    onNotify={({ type, message }) => {
                      if (type === 'success') showSuccess(message);
                      else showWarning(message);
                    }}
                    onChange={handleClientChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData((p) => ({ ...p, nome: e.target.value }))}
                    placeholder="Nome"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Telefone</label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => setFormData((p) => ({ ...p, telefone: e.target.value }))}
                    placeholder="Telefone"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Documento</label>
                  <input
                    type="text"
                    value={formData.documento}
                    onChange={(e) => setFormData((p) => ({ ...p, documento: e.target.value }))}
                    placeholder="CPF/CNPJ"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Endereço</label>
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={(e) => setFormData((p) => ({ ...p, endereco: e.target.value }))}
                    placeholder="Cidade, Estado"
                  />
                </div>
              </div>

              {!!formData.cliente_id && (
                <>
                  <hr />
                  <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Coleta de Destinatário</h3>

                  {!isColetasAdmin && (
                    <p className="input-hint" style={{ marginBottom: '0.75rem' }}>
                      Tarifa padrão (base {DEFAULT_GERENTE_TARIFA_ID}) aplicada automaticamente. Apenas administrador pode alterar a tarifa depois.
                    </p>
                  )}

                  <div className="form-row">
                    <div className="form-group" style={!isColetasAdmin ? { flex: '1 1 100%', minWidth: '100%' } : undefined}>
                      <label>Peso</label>
                      <input
                        type="text"
                        value={formData.peso}
                        onChange={(e) => handlePesoChange(e.target.value)}
                        placeholder="Ex.: 2,5"
                        required
                      />
                    </div>
                    {isColetasAdmin && (
                      <div className="form-group">
                        <label>Selecionar tarifa</label>
                        <Select
                          value={formData.tarifa_id}
                          onChange={(e) => handleTarifaChange(e.target.value)}
                          placeholder="Selecione uma tarifa"
                          options={[
                            { value: '', label: 'Selecione uma tarifa' },
                            ...tarifas.map((t) => ({
                              value: t.id,
                              label: `${t.nome} - ${t.origem} → ${t.destino} (${formatCurrency(
                                parseTarifaNumber(t.valor)
                              )})`,
                            })),
                          ]}
                        />
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Origem</label>
                      <input
                        type="text"
                        value={formData.origem}
                        disabled={isColetasAdmin}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, origem: e.target.value }))
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Destino</label>
                      <input
                        type="text"
                        value={formData.destino}
                        disabled={isColetasAdmin}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, destino: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  {isColetasAdmin && (
                    <div className="form-row">
                      <div className="form-group full-width">
                        <label>Calculo final (Valor da Remessa)</label>
                        <input type="text" value={formData.valor_remessa} disabled />
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-submit" disabled={saving}>
                  {saving
                    ? editingId
                      ? 'Salvando…'
                      : 'Criando…'
                    : editingId
                      ? 'Salvar'
                      : 'Criar Coleta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTargetId}
        title="Excluir coleta"
        message="Tem certeza que deseja excluir esta coleta? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        onConfirm={confirmDeleteColeta}
        onCancel={() => setDeleteTargetId(null)}
      />

      {/* Modal Label */}
      {showLabelModal && selectedColeta && (
        <div className="modal-overlay recipient-print-modal-overlay" onClick={() => setShowLabelModal(false)}>
          <div
            className="modal-content recipient-print-modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 'min(100%, 28rem)', width: '90%' }}
          >
            <div className="modal-header recipient-print-no-print">
              <h3>Etiqueta de Coleta</h3>
              <button type="button" className="btn-icon" onClick={() => setShowLabelModal(false)}>
                <HiX size={20} />
              </button>
            </div>

            <div className="modal-body recipient-print-modal-body coleta-etiqueta-modal-body">
              <p className="coleta-etiqueta-hint recipient-print-no-print">
                Pré-visualização em tamanho real: <strong>50 mm × 26 mm</strong>
              </p>
              <div className="coleta-etiqueta-preview-wrap">
                <div
                  ref={labelRef}
                  id="coleta-print-area"
                  className="coleta-etiqueta-label"
                >
                  <div className="coleta-etiqueta-label__body">
                    {dataColetaEtiquetaStr ? (
                      <div className="coleta-etiqueta-label__row coleta-etiqueta-label__row--date">
                        <span className="coleta-etiqueta-label__date">{dataColetaEtiquetaStr}</span>
                      </div>
                    ) : null}
                    <div className="coleta-etiqueta-label__row coleta-etiqueta-label__row--nome">
                      <span className="coleta-etiqueta-label__nome">
                        {nomeEtiquetaColeta || '—'}
                      </span>
                    </div>
                    <div className="coleta-etiqueta-label__row coleta-etiqueta-label__row--split">
                      <div className="coleta-etiqueta-label__cell">
                        <span className="coleta-etiqueta-label__k">Destino</span>
                        <span className="coleta-etiqueta-label__v">
                          {selectedColeta?.destino?.trim()
                            ? selectedColeta.destino
                            : '—'}
                        </span>
                      </div>
                      <div className="coleta-etiqueta-label__cell coleta-etiqueta-label__cell--peso">
                        <span className="coleta-etiqueta-label__k">Peso</span>
                        <span className="coleta-etiqueta-label__v">
                          {statusForWeight(
                            selectedColeta?.peso_bruto || selectedColeta?.peso || ''
                          )}{' '}
                          kg
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer recipient-print-no-print" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowLabelModal(false)}
              >
                Fechar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveLabelAsPng}
                disabled={savingLabelImage}
              >
                <HiPrinter size={18} />
                {savingLabelImage ? 'Gerando…' : 'Salvar PNG'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coletas;

